// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import {
  Button,
  ComboBox,
  Dropdown,
  ModalFooter,
  RadioButton,
  RadioButtonGroup
} from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import OfferStatusBadge from 'Components/Offers/OfferStatusBadge';
import ProductPreview from './ProductPreview';
import {
  ContentForm,
  PrimaryButton,
  SecondaryButton,
  SubmitLoading
} from './ContentStyles';

const DestinationCard = styled.div`
  border: 1px solid ${ props => props.selected ? '#2f8675' : props.theme.products.colors.border };
  border-radius: 6px;
  margin-top: 0.75rem;
  padding: 0.75rem;

  .cds--radio-button-group {
    margin-bottom: 0.75rem;
  }

  .offer-option {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;

  }
`;

const renderOfferItem = item => (
  <span className="offer-option">
    { item.label }
    <OfferStatusBadge status={ item.status === 'saved' ? 'saved' : 'private' } />
  </span>
);

const SelectDestinationContent = ({
  product,
  destinationType,
  wishlists,
  offers,
  selectedWishlist,
  selectedOffer,
  loading,
  offersLoading,
  wishlistOnly,
  onDestinationTypeChange,
  onWishlistChange,
  onOfferChange,
  onCreateWishlist,
  onCreateOffer,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ContentForm theme={ theme } onSubmit={ onSubmit }>
      <div className="wrapper">
        <ProductPreview product={ product } />
        <DestinationCard theme={ theme } selected={ destinationType === 'wishlist' }>
          <RadioButtonGroup
            legendText={ t('offers.destinationType') }
            name="destinationType"
            valueSelected={ destinationType }
            onChange={ onDestinationTypeChange }
          >
            <RadioButton
              id="destinationWishlist"
              labelText={ t('offers.wishlists') }
              value="wishlist"
            />
            { !wishlistOnly && (
              <RadioButton
                id="destinationOffer"
                labelText={ t('offers.offers') }
                value="offer"
              />
            ) }
          </RadioButtonGroup>
          { destinationType === 'wishlist' && (
            <>
              <Dropdown
                id="destinationWishlistSelect"
                titleText={ t('offers.chooseWishlist') }
                label={ t('offers.chooseWishlist') }
                items={ wishlists }
                selectedItem={ wishlists.find(item => item.id === selectedWishlist) || null }
                onChange={ onWishlistChange }
              />
              <div className="section-heading">
                <span />
                <Button type="button" kind="ghost" onClick={ onCreateWishlist }>
                  + { t('offers.createNewList') }
                </Button>
              </div>
            </>
          ) }
          { destinationType === 'offer' && (
            <>
              <ComboBox
                id="destinationOfferSelect"
                titleText={ t('offers.chooseOffer') }
                placeholder={ t('offers.chooseOffer') }
                items={ offers }
                selectedItem={ offers.find(item => item.id === selectedOffer) || null }
                itemToString={ item => item?.label || '' }
                itemToElement={ renderOfferItem }
                disabled={ offersLoading || !offers.length }
                onChange={ onOfferChange }
              />
              <div className="section-heading">
                <span />
                <Button type="button" kind="ghost" onClick={ onCreateOffer }>
                  + { t('offers.createNewOffer') }
                </Button>
              </div>
            </>
          ) }
        </DestinationCard>
      </div>
      <ModalFooter>
        <PrimaryButton
          className="red submit"
          type="submit"
          disabled={
            loading
            || offersLoading
            || (destinationType === 'wishlist' && !selectedWishlist)
            || (destinationType === 'offer' && !selectedOffer)
          }
        >
          { loading && <SubmitLoading status="active" /> }
          { t('common.agree') }
        </PrimaryButton>
        <SecondaryButton kind="ghost" onClick={ onCancel } disabled={ loading }>
          { t('common.cancel') }
        </SecondaryButton>
      </ModalFooter>
    </ContentForm>
  );
};

SelectDestinationContent.propTypes = {
  product: object,
  destinationType: string,
  wishlists: array,
  offers: array,
  selectedWishlist: string,
  selectedOffer: string,
  loading: bool,
  offersLoading: bool,
  wishlistOnly: bool,
  onDestinationTypeChange: func,
  onWishlistChange: func,
  onOfferChange: func,
  onCreateWishlist: func,
  onCreateOffer: func,
  onSubmit: func,
  onCancel: func
};

export default SelectDestinationContent;
